import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { colors } from '../styles/shared.js';
import { t } from '../i18n.js';
import { cmdCloseOverlays } from '../commands.js';

import './hamburger-button.js';

@customElement('controls-overlay')
export class ControlsOverlay extends LitElement {
  static styles = [
    colors,
    css`
      :host {
        position: absolute;
        inset: 0;
        pointer-events: auto;
        display: flex;
        flex-direction: column;
        padding: max(env(safe-area-inset-top, 0px), 20px) max(env(safe-area-inset-right, 0px), 20px)
          max(env(safe-area-inset-bottom, 0px), 20px) max(env(safe-area-inset-left, 0px), 20px);
        overflow-y: auto;
      }
      .heading {
        color: var(--color-white);
        font-size: 1.7rem;
        font-weight: bold;
        text-align: center;
        margin-bottom: 1.5rem;
        padding-top: 1rem;
      }
      .section-heading {
        color: var(--color-white);
        font-size: 1.15rem;
        font-weight: bold;
        margin-top: 1rem;
        margin-bottom: 0.5rem;
      }
      .control-item {
        color: var(--color-cyan);
        font-size: 1.05rem;
        line-height: 1.6;
        padding-left: 0.5rem;
      }
      .body {
        flex: 1;
        display: flex;
        flex-direction: column;
        max-width: 500px;
        margin: 0 auto;
        width: 100%;
      }
      .dismiss {
        color: #ff0000;
        text-align: center;
        font-size: 1.05rem;
        padding: 1rem;
        cursor: pointer;
      }
    `,
  ];

  @property() accessor variant: 'touch' | 'desktop' = 'desktop';

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', cmdCloseOverlays);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', cmdCloseOverlays);
  }

  render() {
    const isTouch = this.variant === 'touch';
    const prefix = isTouch ? 'touch' : 'desktop';

    return html`
      ${isTouch ? html`<hamburger-button></hamburger-button>` : nothing}
      <div class="heading">${t(`${prefix}-controls-heading`)}</div>
      <div class="body">
        ${this._renderSection(prefix, '2d')}
        ${this._renderSection(prefix, '3d')}
        ${!isTouch ? this._renderOtherSection() : nothing}
      </div>
      <div class="dismiss">
        ${t(`${prefix}-controls-dismiss`)}
      </div>
    `;
  }

  private _renderSection(prefix: string, section: '2d' | '3d') {
    const keys = this._sectionKeys(prefix, section);
    return html`
      <div class="section-heading">${t(`${prefix}-controls-${section}-heading`)}</div>
      ${keys.map((key) => html`<div class="control-item">${t(key)}</div>`)}
    `;
  }

  private _sectionKeys(prefix: string, section: '2d' | '3d'): string[] {
    if (prefix === 'touch') {
      if (section === '2d') {
        return [
          'touch-controls-2d-move',
          'touch-controls-2d-hold',
          'touch-controls-2d-push',
          'touch-controls-2d-enter3d',
        ];
      }
      return [
        'touch-controls-3d-rotate',
        'touch-controls-3d-swipe-h',
        'touch-controls-3d-swipe-v',
        'touch-controls-3d-enter2d',
      ];
    }
    // Desktop
    if (section === '2d') {
      return [
        'desktop-controls-2d-move',
        'desktop-controls-2d-push',
        'desktop-controls-2d-enter3d',
      ];
    }
    return [
      'desktop-controls-3d-rotate',
      'desktop-controls-3d-shift-h',
      'desktop-controls-3d-shift-v',
      'desktop-controls-3d-enter2d',
    ];
  }

  private _renderOtherSection() {
    return html`
      <div class="section-heading">${t('desktop-controls-other-heading')}</div>
      <div class="control-item">${t('desktop-controls-restart')}</div>
      <div class="control-item">${t('desktop-controls-nav')}</div>
    `;
  }
}
